// src/api/pagos.js - API de pagos para la web pública de Jean Informático
// Flujo: El cliente registra su pago en la web -> queda en proceso (PENDIENTE) -> el humano lo verifica en la cuenta bancaria.
import { apiFetch } from './index.js';

/**
 * Crear un pago en estado PENDIENTE asociado a una recepción de equipo.
 * Queda registrado como proceso hasta que el personal administrativo
 * lo verifique contra los movimientos reales de la cuenta bancaria.
 *
 * @param {Object} pagoData
 * @param {number} pagoData.idRecepcionEquipo  - ID de la orden de recepción
 * @param {number} pagoData.monto              - Monto del pago
 * @param {string} pagoData.moneda             - 'BS' o 'USD'
 * @param {string} pagoData.referencia         - Número de referencia del comprobante bancario
 * @param {string} pagoData.descripcion        - Banco emisor / detalles
 * @param {number} [pagoData.idMetodoPago=3]   - ID método pago (3 = Pago Móvil, 4 = Transferencia)
 * @param {number} [pagoData.idUsuario=1]      - ID del usuario del sistema
 * @param {number} [pagoData.idFuenteTasa]     - ID fuente tasa si aplica
 * @param {number} [pagoData.idTasaVenta]      - ID tasa de venta si aplica
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const crearPagoPendiente = async (pagoData) => {
  try {
    if (!pagoData.idRecepcionEquipo) {
      throw new Error('ID de la orden de servicio es requerido');
    }
    if (!pagoData.monto || parseFloat(pagoData.monto) <= 0) {
      throw new Error('El monto es requerido y debe ser mayor a 0');
    }
    if (!pagoData.referencia || !pagoData.referencia.trim()) {
      throw new Error('El número de referencia bancaria es requerido');
    }

    const descLimpia = (pagoData.descripcion || 'Pago registrado por el cliente en la web (Pendiente de verificación bancaria)').trim();
    const descripcionFinal = descLimpia.startsWith('[PAGO WEB]') ? descLimpia : `[PAGO WEB] ${descLimpia}`;

    const payload = {
      fecha_pago: pagoData.fecha_pago || new Date().toISOString(),
      monto: parseFloat(pagoData.monto),
      moneda: (pagoData.moneda || 'BS').toUpperCase(),
      referencia: pagoData.referencia.trim(),
      descripcion: descripcionFinal,
      idMetodoPago: parseInt(pagoData.idMetodoPago || 3),
      idUsuario: parseInt(pagoData.idUsuario || 1),
      idVenta: null,
      idCuota: null,
      idRecepcionEquipo: parseInt(pagoData.idRecepcionEquipo),
      idFuenteTasa: pagoData.idFuenteTasa ? parseInt(pagoData.idFuenteTasa) : null,
      idTasaVenta: pagoData.idTasaVenta ? parseInt(pagoData.idTasaVenta) : null,
      idTasaCompra: null
    };

    console.log('💳 [Web] Registrando pago pendiente en el sistema:', payload);

    const response = await apiFetch('/Pago', {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    return {
      success: true,
      data: response,
      message: 'Pago registrado exitosamente. Está en proceso de verificación por el personal.'
    };
  } catch (error) {
    console.error('❌ Error creando pago pendiente:', error);
    return {
      success: false,
      error: error.message || 'Error al registrar el pago en el servidor',
      data: null
    };
  }
};

/**
 * Obtener pagos registrados para una orden de recepción de equipo.
 * Permite verificar si ya existe un pago pendiente o completado.
 *
 * @param {number} idRecepcion - ID de la recepción
 * @returns {Promise<{success: boolean, data: Array, count: number}>}
 */
export const obtenerPagosPorRecepcion = async (idRecepcion) => {
  try {
    if (!idRecepcion) return { success: false, data: [], count: 0 };
    const response = await apiFetch(`/Pago/recepcion/${idRecepcion}`);
    const pagos = Array.isArray(response) ? response : (response?.data || []);
    return {
      success: true,
      data: pagos,
      count: pagos.length,
      message: 'Pagos obtenidos exitosamente'
    };
  } catch (error) {
    console.warn(`No se pudieron obtener pagos para recepción ${idRecepcion}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener métodos de pago activos desde el backend
 */
export const obtenerMetodosPago = async () => {
  try {
    const response = await apiFetch('/MetodoPago');
    const lista = Array.isArray(response) ? response : (response?.data || []);
    return { success: true, data: lista };
  } catch (error) {
    console.warn('Usando métodos de pago locales:', error);
    return {
      success: false,
      data: [
        { idMetodoPago: 3, nombre: 'Pago Móvil', tipo: 'pago_movil' },
        { idMetodoPago: 4, nombre: 'Transferencia Bancaria', tipo: 'transferencia' },
        { idMetodoPago: 1, nombre: 'Efectivo', tipo: 'efectivo' },
        { idMetodoPago: 7, nombre: 'Zelle', tipo: 'transferencia_internacional' }
      ]
    };
  }
};

export default {
  crearPagoPendiente,
  obtenerPagosPorRecepcion,
  obtenerMetodosPago
};
